import { WorklistItemStatus } from 'generated/graphql';

// Single source of truth for the "Ready to Read" label.
export const READY_TO_READ_LABEL = 'Ready to Read';

const BACKEND_STATUS_TO_ENUM: Partial<Record<string, WorklistItemStatus>> = {
  Queued: WorklistItemStatus.InProgress,
  Locked: WorklistItemStatus.InProgress,
  Countdown: WorklistItemStatus.InProgress,
  'ADNDM Queued': WorklistItemStatus.InProgressAddendum,
  'ADNDM Locked': WorklistItemStatus.InProgressAddendum,
  'ADNDM Countdown': WorklistItemStatus.InProgressAddendum,
  Completed: WorklistItemStatus.Archive,
  Verified: WorklistItemStatus.Pending,
  'Externally Read': WorklistItemStatus.ExternallyRead,
  Mismatched: WorklistItemStatus.Mismatched,
  'In Transit': WorklistItemStatus.InTransit,
  Scheduled: WorklistItemStatus.Scheduled,
};

export type WorklistItemStatusLabels = Record<WorklistItemStatus, string>;

// Explicit map so QC stays an initialism and a new status breaks the build here.
const STATUS_LABELS: WorklistItemStatusLabels = {
  [WorklistItemStatus.Archive]: 'Archive',
  [WorklistItemStatus.ExternallyRead]: 'Externally Read',
  [WorklistItemStatus.InProgress]: 'In Progress',
  [WorklistItemStatus.InProgressAddendum]: 'In Progress Addendum',
  [WorklistItemStatus.InTransit]: 'In Transit',
  [WorklistItemStatus.Mismatched]: 'Mismatched',
  [WorklistItemStatus.Pending]: 'Pending',
  [WorklistItemStatus.Qc]: 'QC',
  [WorklistItemStatus.Scheduled]: 'Scheduled',
};

export function getWorklistItemStatusLabels({
  isReadyToReadEnabled,
}: {
  isReadyToReadEnabled: boolean;
}): WorklistItemStatusLabels {
  if (!isReadyToReadEnabled) return STATUS_LABELS;
  return {
    ...STATUS_LABELS,
    [WorklistItemStatus.Pending]: READY_TO_READ_LABEL,
  };
}

// The Pending worklist is renamed clinic by clinic, so it may have either name
export const PENDING_WORKLIST_NAMES = [
  STATUS_LABELS[WorklistItemStatus.Pending],
  READY_TO_READ_LABEL,
];

function isWorklistItemStatus(value: string): value is WorklistItemStatus {
  return value in STATUS_LABELS;
}

// Label for a status value. Unknown values are shown as-is
export function getWorklistItemStatusLabel({
  status,
  labels,
}: {
  status: Nullable<string>;
  labels: WorklistItemStatusLabels;
}): string {
  if (!status) return '';
  if (isWorklistItemStatus(status)) return labels[status];
  const mappedStatus = BACKEND_STATUS_TO_ENUM[status];
  return mappedStatus ? labels[mappedStatus] : status;
}
